import {gsap} from "gsap";
import {APIEventSystem} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {KennethMustDie} from "../../../game/KennethMustDie";
import {KMDSymbol} from "../../../game/reels/KMDSymbol";
import {
    IFeatureInfo,
    KMD_SYMBOL_ANIMATIONS,
    SPECIAL_SYMBOL_NAMES,
    X_HOLE_ANIMATIONS
} from "../../../game/server/IKMDData";
import {KMDSound} from "../../../game/sound/KMDSound";
import {KMDGameMode} from "../../../game/server/KMDGameMode";

export interface Positionable {
    position: {
        x: number,
        y: number
    }
}

export interface SimpleReelModelSymbolPosition {
    reelID: number;
    position: number
}

export interface SimpleReelModelDelegate {

    createSymbol(symbolName: string, state: SimpleReelModelState, position: SimpleReelModelSymbolPosition): Positionable;

    removeSymbol(symbolData: SimpleReelModelSymbolData): void;

    removeAvalancheSymbol(symbolData: SimpleReelModelSymbolData): void;
}

export interface SimpleReelModelSymbolData {
    name: string;
    positionable: Positionable;
    reelPosition: number;
    isRemoved: boolean;
}

export enum SimpleReelModelState {
    IDLE = "IDLE",
    START = "START",
    SPIN = "SPIN",
    STOP = "STOP",
    STOPPED = "STOPPED",
    BOUNCE = "BOUNCE"
}

export interface SimpleReelModelEventData {
    reel: number;
    state: SimpleReelModelState;
}

interface SimpleReelPaddingData {
    visualPaddingBefore: number;
    paddingBefore: number;
    visibleSymbols: number;
    paddingAfter: number;
    visualPaddingAfter: number;
}

interface IAnimatedStep {
    x: number;
    y: number;
}

export class SimpleReelModel {
    public paddingConfig: SimpleReelPaddingData = {
        visualPaddingBefore: 1,
        paddingBefore: 0,
        visibleSymbols: 3,
        paddingAfter: 0,
        visualPaddingAfter: 1
    };
    public animatedStepData: IAnimatedStep = {
        x: 0,
        y: 0
    };
    public stepSize: number;
    public readonly id: number;
    /**
     * Triggers on: ReelStateChange and each ReelModelState
     *
     *
     */
    public readonly events: APIEventSystem | undefined;
    protected _direction: string;
    private _numOfStickySymbols: number = 0;
    private _reelSetIndex: number = 0;
    private _stopIndex: number;
    private _spinning: boolean = false;
    private _stopping: boolean = false;
    private _startedReverse: boolean;
    private _masterSpinTimeline: gsap.core.Timeline | undefined;
    private _delegate: SimpleReelModelDelegate;
    private _stopTimeline: gsap.core.Timeline | undefined;
    private _symbols: SimpleReelModelSymbolData[] = [];
    private _stepDuration: number = 0.06;
    private _xHTeaseYMover: IAnimatedStep = {
        x: 0,
        y: 0
    };

    constructor(id: number, delegate: SimpleReelModelDelegate) {
        this.id = id;
        this._delegate = delegate;

        this.events = KennethMustDie.apiPlugin.eventSystemFactory.create();
        this.events.on("ReelStateChange", (data: SimpleReelModelEventData) => this.onReelStateChange(data)); //To prevent event system from saving once callbacks if no on callbacks is set.

        this._direction = "vertical";
        this.stepSize = KennethMustDie.SYMBOL_HEIGHT;
    }

    private _avalancheSymbolsRemoved: number = 0;

    get avalancheSymbolsRemoved(): number {
        return this._avalancheSymbolsRemoved;
    }

    set avalancheSymbolsRemoved(value: number) {
        this._avalancheSymbolsRemoved = value;
    }

    // Fake reel
    private _reelSet: string[] | undefined;

    get reelSet(): string[] {
        if (!this._reelSet) {
            throw new Error(`No reelset set for reel: ${this.id}`);
        }
        return this._reelSet;
    }

    private _stopSymbols: SimpleReelModelSymbolData[] = [];

    get stopSymbols(): SimpleReelModelSymbolData[] {
        return this._stopSymbols;
    }

    private _state: SimpleReelModelState = SimpleReelModelState.IDLE;

    get state(): SimpleReelModelState {
        return this._state;
    }

    set state(value: SimpleReelModelState) {
        this._state = value;
        if (this.events) {
            this.events.trigger("ReelStateChange", <SimpleReelModelEventData>{reel: this.id, state: this._state});
        }
    }

    public setReelSet(reelSet: string[], index?: number): void {
        this._reelSet = reelSet;
        index = index != undefined ? index : Math.floor(Math.random() * this._reelSet.length);
        this.setReelIndex(index);
    }

    public init(symbols: string[]): void {
        this.clearSymbols(this._symbols);
        this.clearSymbols(this._stopSymbols);
        for (let i: number = 0; i < symbols.length; i++) {
            if (symbols[i] !== SPECIAL_SYMBOL_NAMES.X) {
                const symbol: SimpleReelModelSymbolData = this.createNewSymbol(symbols[i], i, SimpleReelModelState.IDLE);
                this._symbols.push(symbol);
                this._stopSymbols.push(symbol);
            } else if (symbols[i] === SPECIAL_SYMBOL_NAMES.X) {
                this._avalancheSymbolsRemoved++;
            }
        }
        if ((this.id == 0 || this.id == 4) && this._avalancheSymbolsRemoved > 0) {
            this._avalancheSymbolsRemoved--;
        }
        this.updatePosition();

        // #354, for all the sticky things, only in main reels, not the special bottom row which has reel id 5.
        if (this.id !== KennethMustDie.REELS_NUM) {
            this._numOfStickySymbols = (KennethMustDie.model.reSpawnPositions[this.id].filter((val: boolean) => {
                return val;
            }).length);
        }
    }

    public start(reverse: boolean): gsap.core.Timeline {
        this._startedReverse = reverse;
        const tl: gsap.core.Timeline = gsap.timeline({
            onStart: () => {
                this.state = SimpleReelModelState.START;
                if (this.id === 0 || this.id === 4) {
                    if (this._stopSymbols.length === 2) {
                        this._stopSymbols.unshift(this.createNewSymbol(this.getRandomSymbolInReelSet(), 0, SimpleReelModelState.IDLE));
                    }
                }

                this._symbols = this._stopSymbols;

                if (this._direction === "vertical") {
                    this._symbols.unshift(this.createNewSymbol(this.getRandomSymbolInReelSet(), -1, SimpleReelModelState.IDLE));
                    this._symbols.push(this.createNewSymbol(this.getRandomSymbolInReelSet(), 3, SimpleReelModelState.IDLE));
                }

                this._stopSymbols = [];
            },
            onComplete: () => {
                if (this._direction === "vertical") {
                    this.startSpin(reverse);
                }
            }
        });

        this._stopSymbols.forEach((val: SimpleReelModelSymbolData) => {
            const symbol: KMDSymbol = (val.positionable as KMDSymbol);

            // #351
            symbol.m1m2NameHelperXNudge = "";

            if (symbol.resizeLevel || symbol.getExtrasLength()) {
                symbol.revertResize((this.id === (KennethMustDie.REELS_NUM) ? KennethMustDie.model.data.multiplier[val.reelPosition + 1][3] : KennethMustDie.model.data.multiplier[this.id][val.reelPosition]));
            }
        });

        if (this._direction !== "vertical") {
            for (let i: number = 0; i < this._stopSymbols.length; i++) {
                const symbol: SimpleReelModelSymbolData = this._stopSymbols[i];

                tl.add(gsap.to(symbol.positionable.position, {
                    duration: 0.2,
                    x: symbol.positionable.position.x - (this.stepSize * 3),
                    ease: "none",
                    onComplete: () => {
                        this._delegate.removeSymbol(symbol);
                    }
                }), 0);
            }

            tl.add(() => {
                this.animatedStepData.x = 0;
                this.animatedStepData.y = 0;

                this.clearSymbols(this._symbols);
            })
        } else {
            tl.add(this.step(reverse));
        }

        return tl;
    }

    public stop(stopSymbolNames: string[]): gsap.core.Timeline {
        const reverse: boolean = this._startedReverse,
            numberOfNormalSteps: number = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter + this.paddingConfig.visualPaddingAfter;

        this.clearSymbols(this._stopSymbols);

        this._stopSymbols = [];
        stopSymbolNames = this.getRandomSymbols(this.paddingConfig.visualPaddingBefore).concat(stopSymbolNames);
        stopSymbolNames = stopSymbolNames.concat(this.getRandomSymbols(this.paddingConfig.visualPaddingAfter));

        for (let i: number = 0; i < stopSymbolNames.length; i++) {
            const symbol: SimpleReelModelSymbolData = this.createNewSymbol(stopSymbolNames[i], i - this.paddingConfig.visualPaddingBefore - this.paddingConfig.paddingBefore, SimpleReelModelState.STOP);

            if (this._direction === "vertical") {
                symbol.positionable.position.y = this.stepSize * -(this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols);
            }

            if (stopSymbolNames[i] === SPECIAL_SYMBOL_NAMES.XH) {
                (symbol.positionable as KMDSymbol).name = SPECIAL_SYMBOL_NAMES.XH;
                (symbol.positionable as KMDSymbol).setState(KMD_SYMBOL_ANIMATIONS.NORMAL);
            }

            this._stopSymbols.push(symbol);
        }

        this._stopTimeline = gsap.timeline({
            paused: true,
            onStart: () => {
                this.state = SimpleReelModelState.STOP;
                this._spinning = false;

                this._masterSpinTimeline?.totalProgress(1);

                this._stopIndex = stopSymbolNames.length - 1;
                this._stopping = true;
            },
            onComplete: () => {
                this.state = SimpleReelModelState.STOPPED;
                this._stopTimeline = undefined;
                this._stopping = false;
                this.state = SimpleReelModelState.IDLE;
            }
        });

        for (let i: number = 0; i < numberOfNormalSteps; i++) {
            this._stopTimeline.add(this.step(reverse));
        }

        this._stopTimeline.add([
            () => {
                // todo hack
                this._stopSymbols.forEach((sym: SimpleReelModelSymbolData, idx: number) => {
                    sym.reelPosition = (idx - 1);
                });
            },
            this.bounce()
        ]);

        if (this.id === (KennethMustDie.REELS_NUM - 1)) {
            const s2Feature: IFeatureInfo = KennethMustDie.model.s2Feature;

            if (s2Feature.originReelId !== -1) {
                this._stopTimeline.add(() => {
                    const s2Symbol: KMDSymbol = (KennethMustDie.reelView.reelModels[s2Feature.originReelId].getStopSymbolAt(s2Feature.originSymId + 1).positionable as KMDSymbol);

                    s2Symbol.originalName = SPECIAL_SYMBOL_NAMES.S2;
                    s2Symbol.updateTexture(SPECIAL_SYMBOL_NAMES.S2);

                    KennethMustDie.stickyS2.hideStickyS2();
                });
                this._stopTimeline.to(this, {
                    duration: 0.1
                });
            }
        }

        this._stopTimeline.add(() => {
            KennethMustDie.stickyWilds.changeToReelSymbol(this.id, false);
            KennethMustDie.stickyXNudges.changeToReelSymbol(this.id);
        });

        return this._stopTimeline.play(0);
    }

    public createStopSymbolsForSpecialReel(): void {
        const stopSymbolNames: string[] = KennethMustDie.model.gameData.specialReels;

        this.clearSymbols(this._stopSymbols);

        this._symbols = [];
        this._stopSymbols = [];
        for (let i: number = 1; i <= stopSymbolNames.length; i++) {
            const symbol: SimpleReelModelSymbolData = this.createNewSymbol(stopSymbolNames[i - 1], i - 1, SimpleReelModelState.STOP);

            symbol.positionable.position.x = (this.stepSize * i) + (this.stepSize * 2) + 10; // this 10 is a buffer value, so that symbol is not seen out of the mask
            this._stopSymbols.push(symbol);
        }
    }

    public createFillUpStopSymbols(): void {
        const reel: string[] = KennethMustDie.model.gameData.reels[this.id];

        for (let i: number = this._avalancheSymbolsRemoved; i > 0; i--) {
            let reelPosition: number = ((this.id === 0 || this.id === 4) ? i : (i - 1));

            if (this._numOfStickySymbols > 0) {
                if (this._numOfStickySymbols === 1 && (KennethMustDie.stickyWilds.checkIfSticky(this.id, reelPosition) || KennethMustDie.stickyWilds.checkIfSticky(this.id, 0))) {
                    reelPosition++;
                } else if (this._numOfStickySymbols === 2) {
                    if (KennethMustDie.stickyWilds.checkIfSticky(this.id, 0)) {
                        reelPosition++;

                        if (KennethMustDie.stickyWilds.checkIfSticky(this.id, 1)) {
                            reelPosition++;
                        }
                    }
                }

                // in case of sticky xNudge, only this condition is possible, otherwise entire reel is sticky with xNudge
                if (this._numOfStickySymbols === 2 && KennethMustDie.stickyXNudges.checkIfSticky(this.id, 1)) {
                    reelPosition = 2;
                }

                // in case of sticky S2, there can only be 1 symbol sticky which is S2 on that reel and if the S2 is sticky on 1st row
                if (this.id === 4 && KennethMustDie.model.s2Feature.originReelId !== -1 && KennethMustDie.model.reSpawnPositions[this.id][1]) {
                    reelPosition = 2;
                }

                // in case of 2 S1 feature
                const s1RowId: number = KennethMustDie.model.s1Positions[this.id];
                if (s1RowId > -1 && s1RowId <= reelPosition) {
                    if (s1RowId < 2) {
                        reelPosition++;
                    }
                }
            }

            const symbol: SimpleReelModelSymbolData = this.createNewSymbol(reel[reelPosition], reelPosition, SimpleReelModelState.IDLE);

            symbol.positionable.position.y = -((2 + (2 - reelPosition)) * KennethMustDie.SYMBOL_HEIGHT);
            this._stopSymbols.unshift(symbol);
        }

        this._stopSymbols.sort((a: SimpleReelModelSymbolData, b: SimpleReelModelSymbolData) => {
            return (a.reelPosition < b.reelPosition) ? -1 : 1;
        });
    }

    public createFillUpStopSymbolsForSpecialReel(): void {
        const reel: string[] = KennethMustDie.model.gameData.specialReels;

        for (let i: number = this._avalancheSymbolsRemoved; i > 0; i--) {
            let reelPosition: number = (reel.length - i);

            const symbol: SimpleReelModelSymbolData = this.createNewSymbol(reel[reelPosition], reelPosition, SimpleReelModelState.IDLE);

            symbol.positionable.position.x = ((3 + (2 - reelPosition)) * KennethMustDie.SYMBOL_WIDTH) + 10;
            this.stopSymbols.push(symbol);
        }

        this._stopSymbols.sort((a: SimpleReelModelSymbolData, b: SimpleReelModelSymbolData) => {
            return (a.reelPosition < b.reelPosition) ? -1 : 1;
        });
    }

    public getStopSymbolAt(symbolIndex: number): SimpleReelModelSymbolData {
        return this._stopSymbols[symbolIndex];
    }

    public removeAvalancheSymbol(symbol: SimpleReelModelSymbolData): void {
        if (symbol.isRemoved) {
            return;
        }

        symbol.isRemoved = true;
        this._avalancheSymbolsRemoved++;
        this._delegate.removeAvalancheSymbol(symbol);
    }

    public removeBufferSymbolsFromStopSymbols(): void {
        this._delegate.removeAvalancheSymbol(this._stopSymbols[0]);
        this._delegate.removeAvalancheSymbol(this._stopSymbols[this._stopSymbols.length - 1]);
        this._stopSymbols.shift();
        this._stopSymbols.pop();

        if (this.id === 0 || this.id === 4) {
            this._delegate.removeAvalancheSymbol(this._stopSymbols[0]);
            this._stopSymbols.shift();
        }
    }

    public collapse(): void {
        this._stopSymbols.forEach((val: SimpleReelModelSymbolData, symId: number) => {
            const symbol: KMDSymbol = (val.positionable as KMDSymbol);

            // #351
            symbol.m1m2NameHelperXNudge = "";

            if (!val.isRemoved) {
                symbol.revertResize((this.id === (KennethMustDie.REELS_NUM) ? KennethMustDie.model.gameData.multiplier[symId + 1][3] : KennethMustDie.model.gameData.multiplier[this.id][val.reelPosition]));
            }

            // don't put this in tl.
            if (this._direction === "vertical" && symbol.originalName === SPECIAL_SYMBOL_NAMES.X_NUDGE_WILD) {
                KennethMustDie.stickyXNudges.showStickyXNudge(this.id, val.reelPosition);
            }

            const tl: gsap.core.Timeline = gsap.timeline(),
                multiplier: number = (this.id === KennethMustDie.REELS_NUM ? 0 : KennethMustDie.model.gameData.multiplier[this.id][val.reelPosition]);

            tl.add(() => {
                if (KennethMustDie.model.s1Positions[this.id] > -1 && KennethMustDie.model.reSpawnPositions[this.id][symId]) {
                    symbol.updateTexture(SPECIAL_SYMBOL_NAMES.S, (KennethMustDie.s1CountReelStop === 3 ? 1 : multiplier));
                    symbol.parent.addChild(symbol);
                } else if (symbol.originalName === SPECIAL_SYMBOL_NAMES.S2) {
                    symbol.updateTexture(SPECIAL_SYMBOL_NAMES.S2, multiplier);

                    KennethMustDie.stickyS2.showLock(symbol.getY());
                } else if (!val.isRemoved && symbol.originalName === SPECIAL_SYMBOL_NAMES.W) {
                    symbol.updateTexture(SPECIAL_SYMBOL_NAMES.W, multiplier);

                    KennethMustDie.stickyWilds.lockWild(this.id, val.reelPosition);
                }
            });
        });

        if (this._avalancheSymbolsRemoved === 0) {
            return;
        }

        if (this._direction === "vertical") {
            for (let symId: number = 0; symId < 3; symId++) {
                if (KennethMustDie.model.reSpawnPositions[this.id][symId]) {
                    this._numOfStickySymbols++;
                }
            }

            // If i = 0 it's the last symbol which can't fall down
            for (let i: number = this._stopSymbols.length - 1; i > 0; i--) {
                let ctr: number = 1;

                if (this._stopSymbols[i].isRemoved) {
                    for (let j: number = i - 1; j > -1; j--) {
                        if (KennethMustDie.model.reSpawnPositions[this.id][j] || (this.id === 4 && KennethMustDie.model.s2Feature.originReelId !== -1 && KennethMustDie.model.reSpawnPositions[this.id][1])) {
                            ctr++;
                            continue;
                        }

                        if (!this._stopSymbols[j].isRemoved) {
                            this._stopSymbols[j].reelPosition += ctr;
                        }
                    }
                }
            }
        } else {
            // If it's the last symbol which can't go left
            for (let i: number = 0; i < this._stopSymbols.length - 1; i++) {
                if (this._stopSymbols[i].isRemoved) {
                    for (let j: number = i + 1; j < this._stopSymbols.length; j++) {
                        if (!this._stopSymbols[j].isRemoved) {
                            this._stopSymbols[j].reelPosition--;
                        }
                    }
                }
            }
        }

        this._stopSymbols = this._stopSymbols.filter((val: SimpleReelModelSymbolData) => {
            return (!val.isRemoved);
        });

        this.getCollapseAnimation();
    }

    public fillUp(): gsap.core.Timeline {
        return this.getFillUpAnimation();
    }

    // #331
    public setInteractiveSymbols(bool: boolean): void {
        const mode: string = KennethMustDie.model.data.mode;

        // if any of the mode is FS
        if (mode.indexOf("FREESPIN") > -1) {
            return;
        }

        for (let ctr: number = 0; ctr < this._stopSymbols.length; ctr++) {
            const sym: SimpleReelModelSymbolData = this._stopSymbols[ctr],
                reelPosition: number = sym.reelPosition;

            if (KennethMustDie.model.gameData && (KennethMustDie.model.gameData.evaluatedArea[this.id][reelPosition] === SPECIAL_SYMBOL_NAMES.NW)) {
                continue;
            }

            const symbol: KMDSymbol = (sym.positionable as KMDSymbol);
            if (KennethMustDie.model.initData.symbolValues[symbol.name] && (reelPosition > -1 && reelPosition < 4)) {
                symbol.setInteractive(bool);
            }
        }
    }

    private bounce(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            bounceFactor: number = 0.1;
        let overshoot: gsap.core.Tween,
            back: gsap.core.Tween;

        // #568
        if (this.checkForActivatedXH()) {
            const sym: KMDSymbol = KennethMustDie.reelView.reelModels[2].getStopSymbolAt(2).positionable as KMDSymbol;

            // #534
            if (sym.parent) {
                sym.parent.addChild(sym);
            }
            sym.updateAnimation(SPECIAL_SYMBOL_NAMES.XH);

            tl.add([
                sym.setState(X_HOLE_ANIMATIONS.ANTICIPATION).repeat(1),
                this.teaseXHReel(),
                () => {
                    KennethMustDie.soundPlayer.playSound(KMDSound.X_HOLE_BLINKING);
                }
            ]);
            tl.add(() => {
                sym.setState(X_HOLE_ANIMATIONS.ACTIVE);

                KennethMustDie.soundPlayer.stopSound(KMDSound.X_HOLE_BLINKING);
                KennethMustDie.soundPlayer.playSound(KMDSound.X_HOLE_LAND);
            });
        }

        if (this._direction === "vertical") {
            overshoot = gsap.to(this.animatedStepData, {
                y: this.stepSize * bounceFactor,
                duration: 0.042,
                ease: "power2.out",
                onStart: () => {
                    this.state = SimpleReelModelState.BOUNCE;
                },
                onUpdate: () => this.updatePosition()
            });

            back = gsap.to(this.animatedStepData, {
                y: 0,
                duration: 0.042 * 2,
                ease: "power2.out",
                onUpdate: () => this.updatePosition()
            });

            tl.add(overshoot);
            tl.add(back);

            // #485
            if (KennethMustDie.model.s1Positions[this.id] > -1) {
                const sym: KMDSymbol = KennethMustDie.reelView.reelModels[this.id].getStopSymbolAt(KennethMustDie.model.s1Positions[this.id] + 1).positionable as KMDSymbol;

                tl.add(sym.setState(KMD_SYMBOL_ANIMATIONS.LAND));
            }
        }

        return tl;
    }

    // #568
    private checkForActivatedXH(): boolean {
        if (this.id !== 2) {
            return false;
        }

        if (KennethMustDie.model.gameData.mode === KMDGameMode.FREESPIN_SUPER) {
            return false;
        }

        if (KennethMustDie.model.xHPos !== 1) {
            return false;
        }

        if (KennethMustDie.model.gameData.mode === KMDGameMode.FREESPIN || KennethMustDie.model.gameData.mode === KMDGameMode.FREESPIN_COOL) {
            return true;
        }

        return (!KennethMustDie.model.gameData.wasFeatureBuy && KennethMustDie.buyFeatureName !== "X_HOLE" && KennethMustDie.buyFeatureName !== "X_HOLE_GUARANTEED");
    }

    // #568
    private teaseXHReel(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            duration: number = 0.042;

        this._xHTeaseYMover.y = 0;

        tl.to(this._xHTeaseYMover, {
            y: -5,
            duration: duration / 2,
            ease: "none",
            onUpdate: () => this.updatePositionXHTease()
        });
        tl.to(this._xHTeaseYMover, {
            y: 0,
            duration: duration / 2,
            ease: "none",
            onUpdate: () => this.updatePositionXHTease()
        });
        tl.to(this._xHTeaseYMover, {
            y: 5,
            duration: duration / 2,
            ease: "none",
            onUpdate: () => this.updatePositionXHTease()
        });
        tl.to(this._xHTeaseYMover, {
            y: 0,
            duration: duration / 2,
            ease: "none",
            onUpdate: () => this.updatePositionXHTease()
        });

        tl.repeat(7);

        return tl;
    }

    // #568
    private updatePositionXHTease(): void {
        for (let i: number = 0; i < this._symbols.length; i++) {
            const symbol: SimpleReelModelSymbolData = this._symbols[i],
                basePos: number = this.stepSize * symbol.reelPosition;

            if (!(symbol.positionable as KMDSymbol).parent) {
                continue;
            }

            symbol.positionable.position.y = basePos + this._xHTeaseYMover.y;
        }
    }

    private onReelStateChange(data: SimpleReelModelEventData): void {
        this.events?.trigger(data.state, data);
    }

    private getNextSymbolName(nextSymbolPosition: number): string {
        let nextIndex: number = this._reelSetIndex + nextSymbolPosition;
        if (nextIndex < 0) {
            nextIndex = this.reelSet.length + nextIndex;
        }
        if (nextIndex >= this.reelSet.length) {
            nextIndex = nextIndex - this.reelSet.length;
        }
        return this.reelSet[nextIndex];
    }

    private getNextSymbol(position: number, state: SimpleReelModelState): SimpleReelModelSymbolData {
        if (this._stopping && this._stopSymbols.length > 0) {
            const symbol: SimpleReelModelSymbolData = this._stopSymbols[this._stopIndex];
            symbol.reelPosition = position;
            return symbol;
        }
        //Default
        return this.createNewSymbol(this.getNextSymbolName(position), position, state);
    }

    private createNewSymbol(name: string, position: number, state: SimpleReelModelState): SimpleReelModelSymbolData {
        const symbol: SimpleReelModelSymbolData = {
            name: name,
            positionable: this._delegate.createSymbol(name, state, {reelID: this.id, position: position}),
            reelPosition: position,
            isRemoved: false
        };

        return symbol;
    }

    private getRandomSymbolInReelSet(): string {
        const reelSet: string[] = ["L1", "L2", "L3", "L4", "L5", "M1", "M2", "M3", "M4", "M5"], // just regular symbols
            randomIndex: number = Math.floor(Math.random() * reelSet.length);

        return reelSet[randomIndex];
    }

    private clearSymbols(symbols: SimpleReelModelSymbolData[]): void {
        let symbol;
        while (symbol = symbols.pop()) {
            this._delegate.removeSymbol(symbol);
        }
    }

    private getRandomSymbols(count: number): string[] {
        const result: string[] = [];
        for (let i: number = 0; i < count; i++) {
            result.push(this.getRandomSymbolInReelSet());
        }
        return result;
    }

    private startSpin(reverse: boolean = false): void {
        this._spinning = true;
        this.state = SimpleReelModelState.SPIN;
        this._masterSpinTimeline = gsap.timeline();
        this._masterSpinTimeline.add(this.spin(reverse));
    }

    private spin(reverse: boolean = false): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
            onComplete: () => {
                if (this._spinning) {
                    this._masterSpinTimeline?.add(this.spin(reverse));
                }
            }
        });

        tl.add(this.step(reverse));

        return tl;
    }

    private step(reverse: boolean, duration: number = this._stepDuration): gsap.core.Timeline {
        let tween: gsap.core.Timeline = gsap.timeline();

        tween.to(this.animatedStepData, {
            y: reverse ? -this.stepSize : this.stepSize,
            duration: duration,
            ease: "none",
            onStart: () => this.onAnimationStart(reverse),
            onUpdate: () => this.updatePosition(),
            onComplete: () => this.onAnimationComplete(reverse)
        });

        return tween;
    }

    private onAnimationStart(reverse: boolean): void {
        this.addSymbol(reverse);
    }

    private onAnimationComplete(reverse: boolean): void {
        for (let i: number = 0; i < this._symbols.length; i++) {
            const symbol: SimpleReelModelSymbolData = this._symbols[i];
            symbol.reelPosition += reverse ? -1 : 1;
        }

        this.animatedStepData.x = 0;
        this.animatedStepData.y = 0;

        this.updateIndex(reverse);
        this.removeSymbol();
        this.updatePosition();
    }

    private updateIndex(reverse: boolean): void {
        const valueChange: number = reverse ? 1 : -1;
        if (this._stopping) {
            this.setStopIndex(this._stopIndex + valueChange);
        } else {
            this.setReelIndex(this._reelSetIndex + valueChange);
        }
    }

    private setStopIndex(value: number): void {
        if (value >= this._stopSymbols.length) {
            value = 0;
        } else if (value < 0) {
            value = this._stopSymbols.length - 1;
        }
        this._stopIndex = value;
    }

    private setReelIndex(value: number): void {
        if (value >= this.reelSet.length) {
            value = 0;
        } else if (value < 0) {
            value = this.reelSet.length - 1;
        }
        this._reelSetIndex = value;
    }

    private addSymbol(reverse: boolean): void {
        let newSymbols: SimpleReelModelSymbolData[] = [];
        if (reverse) {
            return;
        } else {
            const newSymbolPosition: number = this._symbols[0].reelPosition - 1;
            newSymbols.push(this.getNextSymbol(newSymbolPosition, this.state));
        }

        this._symbols = this._symbols.concat(newSymbols);
        this._symbols.sort((a: SimpleReelModelSymbolData, b: SimpleReelModelSymbolData) => {
            return (a.reelPosition < b.reelPosition) ? -1 : 1;
        });
    }

    private removeSymbol(): void {
        for (let i: number = this._symbols.length - 1; i >= 0; i--) {
            const symbol: SimpleReelModelSymbolData = this._symbols[i];
            if (symbol.reelPosition >= this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter + this.paddingConfig.visualPaddingAfter) {
                //Remove from bottom
                const removedSymbol: SimpleReelModelSymbolData = this._symbols.splice(i, 1)[0];
                this._delegate.removeSymbol(removedSymbol);
            }
            if (symbol.reelPosition < -this.paddingConfig.paddingBefore - this.paddingConfig.visualPaddingBefore) {
                //Remove from top
                const removedSymbol: SimpleReelModelSymbolData = this._symbols.splice(i, 1)[0];
                this._delegate.removeSymbol(removedSymbol);
            }
        }
    }

    private updatePosition(): void {
        for (let i: number = 0; i < this._symbols.length; i++) {
            const symbol: SimpleReelModelSymbolData = this._symbols[i],
                basePos: number = this.stepSize * symbol.reelPosition;

            if (!(symbol.positionable as KMDSymbol).parent) {
                continue;
            }

            if (this._direction === "vertical") {
                symbol.positionable.position.y = basePos + this.animatedStepData.y;
            } else {
                symbol.positionable.position.x = basePos + this.animatedStepData.x;
            }
        }
    }

    private getCollapseAnimation(): void {
        const collapseTimeline: gsap.core.Timeline = gsap.timeline(),
            subTimelines: gsap.core.Timeline[] = [];
        let timeline: gsap.core.Timeline,
            specialRowCollapseSoundPlayed: boolean = false;

        this._stopSymbols.reverse();
        for (let i: number = 0; i < this._stopSymbols.length; i++) {
            const symbol: SimpleReelModelSymbolData = this._stopSymbols[i];
            if (!symbol) {
                throw new Error(`getCollapseAnimation, symbol is null!`);
            } else {
                timeline = gsap.timeline();
                if (this._direction === "vertical" && symbol.positionable.position.y !== (this.stepSize * symbol.reelPosition)) {
                    timeline.to(symbol.positionable.position, {
                        duration: 0.2,
                        y: (this.stepSize * symbol.reelPosition),
                        ease: "none"
                    });

                    // #534
                    if (symbol.name === SPECIAL_SYMBOL_NAMES.XH && symbol.reelPosition === 1) {
                        const xHSym: KMDSymbol = (symbol.positionable as KMDSymbol);

                        timeline.add([
                            () => {
                                if (xHSym.parent) {
                                    xHSym.parent.addChild(xHSym);
                                }

                                KennethMustDie.soundPlayer.playSound(KMDSound.X_HOLE_LAND);
                            },
                            xHSym.setState(X_HOLE_ANIMATIONS.ACTIVE)
                        ]);
                    }

                    // #460
                    KennethMustDie.canPlayCollapseSoundInMainReels = true;
                } else if (this._direction === "horizontal" && symbol.positionable.position.x !== (this.stepSize * symbol.reelPosition)) {
                    timeline.to(symbol.positionable.position, {
                        duration: 0.333,
                        x: (this.stepSize * symbol.reelPosition),
                        ease: "none"
                    });

                    // #460
                    if (!specialRowCollapseSoundPlayed) {
                        specialRowCollapseSoundPlayed = true;
                        KennethMustDie.soundPlayer.playSound(KMDSound.BASEMENT_REEL_STOP);
                    }
                }
                subTimelines.push(timeline);
            }
        }
        this._stopSymbols.reverse();
        collapseTimeline.add(subTimelines);
    }

    private getFillUpAnimation(): gsap.core.Timeline {
        const fillUpTimeline: gsap.core.Timeline = gsap.timeline(),
            subTimelines: gsap.core.Timeline[] = [];
        let timeline: gsap.core.Timeline;

        this._numOfStickySymbols = 0;

        if (this._avalancheSymbolsRemoved === 0) {
            return fillUpTimeline;
        }
        let soundPlayed: boolean = false;
        const duration: number = 0.2;
        for (let i: number = 0; i < this._stopSymbols.length; i++) {
            const symbol: SimpleReelModelSymbolData = this._stopSymbols[i];
            if (!symbol) {
                throw new Error(`getFillUpAnimation, symbol is null!`);
            } else {
                const sym: KMDSymbol = (symbol.positionable as KMDSymbol);
                if (sym.position.y !== (this.stepSize * symbol.reelPosition)) {
                    timeline = gsap.timeline();

                    // #485
                    if (sym.originalName === SPECIAL_SYMBOL_NAMES.S) {
                        timeline.add(sym.setState(KMD_SYMBOL_ANIMATIONS.LAND));
                    }

                    timeline.to(sym.position, {
                        duration: duration,
                        y: (this.stepSize * symbol.reelPosition),
                        ease: "none"
                    }, "<");

                    timeline.add(() => {
                        // #226, #240
                        if (sym.originalName === SPECIAL_SYMBOL_NAMES.X_NUDGE_WILD) {
                            KennethMustDie.soundPlayer.playSound(KMDSound.X_NUDGE_LAND);

                            // #273
                            sym.showMultiplierOnXNudge(1, -1);
                        } else if (sym.originalName === SPECIAL_SYMBOL_NAMES.X_WAYS) {
                            KennethMustDie.soundPlayer.playSound(KMDSound.X_WAYS_LAND);
                        } else if (sym.originalName === SPECIAL_SYMBOL_NAMES.S) {
                            KennethMustDie.soundPlayer.playSound(KMDSound.SCATTER_LAND[KennethMustDie.s1CountReelStop]);

                            KennethMustDie.s1CountReelStop++;
                            if (KennethMustDie.s1CountReelStop === 3) {
                                KennethMustDie.model.s1Positions.forEach((val: number, reelId: number) => {
                                    if (val > -1) {
                                        (KennethMustDie.reelView.reelModels[reelId].getStopSymbolAt(val).positionable as KMDSymbol).hideSymbolCount();
                                    }
                                });

                                // #529
                                KennethMustDie.soundPlayer.playSound(KMDSound.SCATTER_FIRE);
                                // #485
                                KennethMustDie.nearWinAnticipation.playAnticipateFireAnimation(this.id, KennethMustDie.model.s1Positions[this.id] + 1, 0);
                            }
                        } else if (sym.originalName === SPECIAL_SYMBOL_NAMES.XH) {
                            KennethMustDie.soundPlayer.playSound(KennethMustDie.model.xHPos === 1 ? KMDSound.X_HOLE_LAND : KMDSound.X_HOLE_LAND_MISS);

                            // #534
                            const xHSymbol: KMDSymbol = (KennethMustDie.reelView.reelModels[2].getStopSymbolAt(KennethMustDie.model.xHPos).positionable as KMDSymbol);
                            if (xHSymbol.parent) {
                                xHSymbol.parent.addChild(xHSymbol);
                            }
                            xHSymbol.updateAnimation(SPECIAL_SYMBOL_NAMES.XH);
                            if (KennethMustDie.model.xHPos === 1) {
                                xHSymbol.setState(X_HOLE_ANIMATIONS.ACTIVE);
                            }
                        } else if (sym.originalName === SPECIAL_SYMBOL_NAMES.S2 && KennethMustDie.s1CountReelStop === 3) {
                            KennethMustDie.soundPlayer.playSound(KMDSound.SCATTER_LAND[3]);

                            // #485
                            if (!KennethMustDie.model.gameData.wasFeatureBuy) {
                                // #529
                                KennethMustDie.soundPlayer.playSound(KMDSound.SCATTER_FIRE);

                                KennethMustDie.nearWinAnticipation.playAnticipateFireAnimation(4, KennethMustDie.model.s2Feature.originSymId + 1, 0);
                            }
                        } else {
                            // #238, #460, #514
                            if (!soundPlayed) {
                                KennethMustDie.soundPlayer.playSound(KMDSound.AVALANCHE_SYMBOL_DROP_IN);

                                soundPlayed = true;
                            }

                            // #517
                            if (sym.originalName === SPECIAL_SYMBOL_NAMES.SM1 || sym.originalName === SPECIAL_SYMBOL_NAMES.SM2) {
                                sym.parent.addChild(sym);
                            }
                        }
                    });

                    subTimelines.push(timeline);
                }
            }
        }

        this._avalancheSymbolsRemoved = 0;

        fillUpTimeline.add(subTimelines);

        return fillUpTimeline;
    }
}